import { el, clear } from "../components/dom.js";
import { searchBar } from "../components/search-bar.js";
import { expandableResultCard } from "../components/search-result.js";
import { searchMovies, getMovie } from "../api/movies.js";
import {
	addMovie,
	deleteMovieByImdbId,
	listMovies,
	formatRuntime,
} from "../db/movies.js";
import { navigateBack } from "../main.js";
import { magScreenHead, MAG } from "../components/magazine/kit.js";

// Add a movie: search OMDb by title (or paste an IMDb id / link — the worker
// looks it up directly), expand a result to read the plot + details, then Add.
// OMDb's search rows carry only title / year / poster, so the full record is
// fetched lazily: on expand (to show it) or on Add (to store it) — one OMDb call
// per film either way, cached for the screen's lifetime.

export async function renderAddMovie(root) {
	const head = magScreenHead("Add a Movie", {
		eyebrow: "Find & collect",
		eyebrowColor: "#e5383b",
		onBack: () => navigateBack("/movies"),
	});

	const resultsContainer = el("div", { style: { display: "flex", flexDirection: "column", gap: "9px", padding: "6px 0 24px" } });
	const statusLine = el("div", {
		style: {
			fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "9.5px",
			letterSpacing: ".14em", textTransform: "uppercase", color: MAG.lime, padding: "10px 0 4px",
		},
	});

	const existing = await listMovies();
	const addedIds = new Set(existing.map((m) => m.imdb_id));

	let inflight = null;
	let lastResults = [];
	const fullById = new Map(); // imdb_id -> full OMDb record

	async function fullRecord(imdbId) {
		if (fullById.has(imdbId)) return fullById.get(imdbId);
		const rec = await getMovie(imdbId);
		fullById.set(imdbId, rec);
		return rec;
	}

	function detailChips(full) {
		return [
			{ label: "Runtime", value: formatRuntime(full.runtime) ?? "" },
			{ label: "Rated", value: full.rated ?? "" },
			{ label: "Genres", value: (full.genres ?? []).join(", ") },
			{ label: "Director", value: (full.directors ?? []).join(", ") },
			{ label: "Cast", value: (full.cast ?? []).join(", ") },
			{ label: "IMDb", value: full.imdb_rating != null ? `IMDb ${full.imdb_rating}` : "" },
		];
	}

	function renderResults() {
		clear(resultsContainer);
		for (const result of lastResults) {
			resultsContainer.append(
				expandableResultCard({
					cover_url: result.cover_url,
					mediaType: "movie",
					title: result.title,
					subtitle: result.release_year ? String(result.release_year) : "",
					summary: "",
					details: [],
					loadDetails: async () => {
						const full = await fullRecord(result.imdb_id);
						return { summary: full.summary ?? "", details: detailChips(full) };
					},
					added: addedIds.has(result.imdb_id),
					onToggleAdd: (want) => toggle(result, want),
				}),
			);
		}
	}

	function friendlyError(err) {
		if (err?.status === 429) return "OMDb's daily limit is reached — try again tomorrow.";
		return err?.message ?? String(err);
	}

	// Returns the resulting added-state so the card can repaint in place.
	async function toggle(result, want) {
		try {
			if (!want) {
				await deleteMovieByImdbId(result.imdb_id);
				addedIds.delete(result.imdb_id);
				return false;
			}
			const full = await fullRecord(result.imdb_id);
			await addMovie({
				imdb_id: result.imdb_id,
				title: full.title ?? result.title,
				release_year: full.release_year ?? result.release_year ?? null,
				release_date: full.release_date ?? null,
				cover_url: full.cover_url ?? result.cover_url ?? null,
				summary: full.summary ?? null,
				runtime: full.runtime ?? null,
				rated: full.rated ?? null,
				genres: full.genres ?? [],
				directors: full.directors ?? [],
				writers: full.writers ?? [],
				cast: full.cast ?? [],
				imdb_rating: full.imdb_rating ?? null,
			});
			addedIds.add(result.imdb_id);
			return true;
		} catch (err) {
			statusLine.textContent = `Could not ${want ? "add" : "remove"}: ${friendlyError(err)}`;
			throw err;
		}
	}

	async function runSearch(q) {
		if (inflight) inflight.abort();
		lastResults = [];
		clear(resultsContainer);
		statusLine.textContent = "";
		if (!q) return;

		const controller = new AbortController();
		inflight = controller;
		statusLine.textContent = "Searching…";
		try {
			const results = await searchMovies(q, { signal: controller.signal });
			if (controller.signal.aborted) return;
			lastResults = results;
			statusLine.textContent = results.length
				? `${results.length} result${results.length === 1 ? "" : "s"}`
				: "No results. OMDb matches titles exactly — try fewer words, or paste an IMDb link.";
			renderResults();
		} catch (err) {
			if (err.name === "AbortError") return;
			statusLine.textContent = `Error: ${friendlyError(err)}`;
		} finally {
			if (inflight === controller) inflight = null;
		}
	}

	const bar = searchBar({
		placeholder: "Search movies (title or IMDb link)…",
		onInput: (q) => runSearch(q),
	});

	root.append(
		el(
			"div.view",
			{},
			head,
			el("div", { style: { padding: "14px 0 0" } }, bar.node),
			statusLine,
			resultsContainer,
		),
	);
}
